import * as React from 'react';
import {
  deriveRetainedIds,
  getSettleState,
  type SettleTrackableEntry,
  subscribeSettleState,
} from '@/common/queries/itemsTreeSetSettleState';

const EMPTY_STATE = { pendingIds: new Set<number>(), settledIds: new Set<number>() };
const EMPTY_RETAINED_IDS: ReadonlySet<number> = new Set();
const noopSubscribe = () => () => {};

/**
 * Row-mark state for one mounted tree-set surface. `enabled = false` skips the subscription, so
 * an opted-out table doesn't pay fan-out cost on every markPending/settlePending.
 */
export const useTreeSetSettleState = (
  entries: readonly SettleTrackableEntry[],
  enabled = true,
): {
  pendingIds: ReadonlySet<number>;
  settledIds: ReadonlySet<number>;
  retainedIds: ReadonlySet<number>;
} => {
  const scope = React.useId();

  const subscribe = React.useCallback(
    (listener: () => void) => (enabled ? subscribeSettleState(scope, listener) : noopSubscribe()),
    [scope, enabled],
  );
  const getSnapshot = React.useCallback(
    () => (enabled ? getSettleState(scope) : EMPTY_STATE),
    [scope, enabled],
  );
  const { pendingIds, settledIds } = React.useSyncExternalStore(subscribe, getSnapshot);

  // Ids ever seen pending/settled here, so a row later dropped from the filter is still
  // recognized as this session's edit.
  const everMarkedRef = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    if (!enabled) {
      everMarkedRef.current.clear();
      return;
    }
    // Gated on this table's own entries: the global store fans out to every mounted scope, so
    // without this an id edited in another table would accumulate here forever.
    const byId = new Map(entries.map((entry) => [entry.id, entry]));
    for (const id of pendingIds) if (byId.has(id)) everMarkedRef.current.add(id);
    for (const id of settledIds) if (byId.has(id)) everMarkedRef.current.add(id);

    for (const id of everMarkedRef.current) {
      if (pendingIds.has(id) || settledIds.has(id)) continue;
      const entry = byId.get(id);
      if (!entry || entry.matched) everMarkedRef.current.delete(id);
    }
  }, [pendingIds, settledIds, entries, enabled]);

  // Guarded here too — a ref mutation in the effect above doesn't itself force a re-render.
  const retainedIds = enabled
    ? deriveRetainedIds(everMarkedRef.current, entries, pendingIds, settledIds)
    : EMPTY_RETAINED_IDS;

  return { pendingIds, settledIds, retainedIds };
};
